/**
 * The header's back button, for the installed app: standalone, and in
 * Chrome's title bar (window-controls-overlay), there is no browser back
 * button. Back goes through the app's own history when there is one; opened
 * straight onto a pane (a shared link, a relaunch), it goes up to the list
 * the pane belongs to instead — a ledger pane to the ledger, never home.
 */

import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
  Router
} from 'vue-router'

/** The list a detail pane sits beside, or null on a page of its own. */
export function parentOf(route: RouteLocationNormalizedLoaded): RouteLocationRaw | null {
  const query = route.query
  switch (route.name) {
    case 'link':
      return { name: 'home', query }
    case 'ledger-item':
    case 'ledger-link':
      return { name: 'ledger', query }
    case 'people-pair':
    case 'person':
    case 'people-link':
      return { name: 'people', query }
    case 'calendar-link': {
      const { view, date } = route.params
      return { name: 'calendar', params: { view, date }, query }
    }
    default:
      return null
  }
}

/**
 * The list to slip beneath a pane just arrived at, or null. A ledger item
 * is opened from all over (a to-do in the library, a brief, the palette, the
 * quick add), and a phone shows it alone, on the Ledger tab: back from it is
 * the ledger, whichever way it came in. Not on a back or forward, and not on
 * a fresh load, which has no entry to put it under (the header's back goes
 * up to the list then, parentOf).
 */
export function listBeneath(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  pop: boolean
): RouteLocationRaw | null {
  if (pop || !from.matched.length) return null
  if (to.name !== 'ledger-item' && to.name !== 'ledger-link') return null
  if (from.path === '/ledger' || from.path.startsWith('/ledger/')) return null
  return { name: 'ledger', query: to.query }
}

/**
 * Once, from main.ts, before the app takes the router: the list takes the
 * entry the pane arrived in, and the pane goes on top of it, so a push and a
 * replace (a to-do's note giving way to its item) both end up there.
 */
export function installListBeneath(router: Router) {
  let pop = false
  router.options.history.listen((_to, _from, info) => {
    pop = info.type === 'pop'
  })
  router.afterEach((to, from, failure) => {
    const wasPop = pop
    pop = false
    if (failure) return
    const list = listBeneath(to, from, wasPop)
    if (list) void router.replace(list).then(() => router.push(to.fullPath))
  })
}

/** vue-router keeps the previous in-app entry in history.state; a fresh load has none. */
export function hasHistoryBack(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    typeof (state as { back?: unknown }).back === 'string'
  )
}

/** Installed: no browser chrome, so no back button but ours. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false
  if ((navigator as { standalone?: boolean }).standalone === true) return true
  return (
    typeof matchMedia === 'function' &&
    matchMedia(
      '(display-mode: standalone), (display-mode: window-controls-overlay), (display-mode: fullscreen)'
    ).matches
  )
}
